// src/App.js
import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import WhatsAppButton from "./components/home/WhatsAppButton";
import LoadingSpinner from "./components/common/LoadingSpinner";
import ErrorBoundary from "./components/common/ErrorBoundary";

// Lazy-loaded public pages
const Home = lazy(() => import("./pages/Home"));
const Appointments = lazy(() => import("./pages/Appointments"));
const Services = lazy(() => import("./pages/Services"));
// Gallery page route removed — app stays for hero/about images, route commented out for potential future use
// const Gallery = lazy(() => import("./pages/Gallery"));
const Contact = lazy(() => import("./pages/Contact"));

// Lazy-loaded admin pages
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminAppointments = lazy(() => import("./pages/admin/Appointments"));
const AdminAppointmentDetail = lazy(
  () => import("./pages/admin/AppointmentDetail"),
);
const AdminStudents = lazy(() => import("./pages/admin/Students"));
const AdminStudentDetail = lazy(() => import("./pages/admin/StudentDetail"));
const AdminEnrollments = lazy(() => import("./pages/admin/Enrollments"));
const AdminPayments = lazy(() => import("./pages/admin/Payments"));
const AdminChatQueue = lazy(() => import("./pages/admin/ChatQueue"));
const AdminConversations = lazy(() => import("./pages/admin/Conversations"));
const AdminStaff = lazy(() => import("./pages/admin/Staff"));
const AdminLocations = lazy(() => import("./pages/admin/Locations"));
const AdminServices = lazy(() => import("./pages/admin/Services"));
const AdminAuditLog = lazy(() => import("./pages/admin/AuditLog"));

// Page-level loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <LoadingSpinner size="lg" text="Cargando..." />
  </div>
);

function App() {
  return (
    <Router>
      <div className="flex flex-col min-h-screen bg-white">
        {/* Header with navigation - fixed at top */}
        <Header />

        {/* Main content area with proper spacing from fixed header */}
        <main className="flex-grow pt-20">
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Home page with hero section and overview */}
                <Route path="/" element={<Home />} />

                {/* Appointment booking page - no login required */}
                <Route path="/citas" element={<Appointments />} />

                {/* Detailed services information page */}
                <Route path="/servicios" element={<Services />} />

                {/* Gallery page route removed — app stays for hero/about images, route commented out for potential future use */}
                {/* <Route path="/galeria" element={<Gallery />} /> */}

                {/* Contact information and location */}
                <Route path="/contacto" element={<Contact />} />

                {/* ─── Admin CRM Dashboard Routes ─── */}
                {/* Admin login page - no auth required */}
                <Route path="/admin/login" element={<AdminLogin />} />

                {/* Admin dashboard - auth required */}
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />

                {/* Appointments management */}
                <Route
                  path="/admin/appointments"
                  element={<AdminAppointments />}
                />
                <Route
                  path="/admin/appointments/:id"
                  element={<AdminAppointmentDetail />}
                />

                {/* Students management */}
                <Route path="/admin/students" element={<AdminStudents />} />
                <Route
                  path="/admin/students/:id"
                  element={<AdminStudentDetail />}
                />

                {/* Enrollments management */}
                <Route
                  path="/admin/enrollments"
                  element={<AdminEnrollments />}
                />

                {/* Payments management */}
                <Route path="/admin/payments" element={<AdminPayments />} />

                {/* Chat queue & live conversations */}
                <Route path="/admin/chats" element={<AdminChatQueue />} />
                <Route
                  path="/admin/conversations"
                  element={<AdminConversations />}
                />

                {/* Staff management */}
                <Route path="/admin/staff" element={<AdminStaff />} />

                {/* Locations management */}
                <Route path="/admin/locations" element={<AdminLocations />} />

                {/* Services management */}
                <Route path="/admin/services" element={<AdminServices />} />

                {/* Audit log */}
                <Route path="/admin/audit" element={<AdminAuditLog />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>

        {/* Footer with company info and links */}
        <Footer />

        {/* Floating WhatsApp button for direct messaging */}
        <WhatsAppButton />
      </div>
    </Router>
  );
}

export default App;
